import type {AnyQueryRoute, RouteMatchData} from '@github-ui/react-core/future/query-route'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {hashKey, useQueryClient} from '@github-ui/react-query'
import {useRouteLoaderData} from '@github-ui/react-router'
import type {UpdateSavedViewResponse, UpdateSavedViewVariables} from '@github-ui/saved-views'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useLayoutEffect, useRef} from 'react'

import {isViewConfiguration} from '../components/board/issues/config-payload'
import type {ViewConfiguration} from '../contexts/views/view-configuration'
import {canonicalQueryKey, copyRouteQueryData, requestPathname} from '../helpers/saved-view-route-cache'
import {SAVED_VIEW_URL_PARAMS} from '../utils/saved-view-url-params'
import {useLoadingNavigation} from './use-saved-view-navigation'
import type {SavedViewRequestSearchParams} from './use-saved-view-prefetch'

/** The search-param reader for each route query whose response Save moves to the clean URL, by query name. */
export type SavedViewRouteQueryReaders<Route extends AnyQueryRoute> = {mainQuery: SavedViewRequestSearchParams} & {
  [QueryName in Exclude<keyof Route['queries'], 'mainQuery'>]?: SavedViewRequestSearchParams
}

interface UseClearSavedViewOverridesInput<Route extends AnyQueryRoute> {
  /** The route that serves the view. */
  route: Route
  /** Reads the request each listed query sends for a URL, the same way its `queryDeps` does. */
  requestSearchParams: SavedViewRouteQueryReaders<Route>
  /** Removes the prefetches for the view except `promoted`. Usually from `useEvictSavedViewPrefetches`. */
  evictPrefetches: (promoted?: RouteQueryKey) => void
}

interface SavedViewOverrideHandlers {
  /** Handler that is called with the response and the request when Save succeeds. */
  onSaveSuccess: (
    response: UpdateSavedViewResponse<ViewConfiguration>,
    sent: UpdateSavedViewVariables<ViewConfiguration>,
  ) => void
  /**
   * Handler that is called when an edit to the view is saved. Returns whether the page is moving
   * to another URL, either through this handler when the URL previews a query, or through a
   * navigation to another page that is already loading.
   */
  onEditSuccess: () => boolean
}

/**
 * Returns the handlers a saved view page calls once the view is saved or edited.
 *
 * After Save, the page moves to its URL without the parameters Save made redundant: `q` and
 * `page`, plus the configuration parameters when Save sent a configuration. Other parameters
 * stay, and the hash goes.
 *
 * Before it navigates, each query in `requestSearchParams` has its response on screen copied to
 * the key the new URL loads, with the saved configuration and view on `mainQuery`, so the page
 * keeps what it shows without a request. A query whose request differs at the new URL, such as
 * a list on page two, is not copied and loads again. Prefetches for the view are cleared,
 * keeping the copied `mainQuery` response.
 *
 * After an edit, the saved query may have changed, so nothing is copied. Prefetches for the view
 * are cleared, and a previewed `q` and its `page` leave the URL. When a navigation on the view is
 * loading, such as an option pick, the page moves to that URL without `q`, so the pick stays. A
 * loading navigation to another page is left to finish. The edit handler reads the navigation
 * when it is called, since the edit mutation keeps it from the render where Save was clicked.
 *
 * Needs a data router.
 */
export function useClearSavedViewOverrides<Route extends AnyQueryRoute>({
  route,
  requestSearchParams,
  evictPrefetches,
}: UseClearSavedViewOverridesInput<Route>): SavedViewOverrideHandlers {
  const navigate = useNavigate()
  const navigation = useLoadingNavigation()
  const {pathname, search} = navigation.current
  const navigationRef = useRef(navigation)
  useLayoutEffect(() => {
    navigationRef.current = navigation
  })
  const queryClient = useQueryClient()
  const queries: Partial<Record<string, {queryKey: RouteQueryKey}>> | undefined = useRouteLoaderData<
    RouteMatchData<Route> | undefined
  >(route.id)?.queries

  const onSaveSuccess = useCallback(
    (response: UpdateSavedViewResponse<ViewConfiguration>, sent: UpdateSavedViewVariables<ViewConfiguration>) => {
      const savedConfiguration =
        sent.configuration !== undefined && isViewConfiguration(response.configuration)
          ? response.configuration
          : undefined
      // The URL that now loads the response on screen.
      const unchanged = new URLSearchParams(search)
      unchanged.delete('q')
      if (savedConfiguration) {
        for (const param of SAVED_VIEW_URL_PARAMS) unchanged.delete(param)
      }
      const destination = new URLSearchParams(unchanged)
      destination.delete('page')

      let promoted: RouteQueryKey | undefined
      for (const [queryName, read] of Object.entries<SavedViewRequestSearchParams | undefined>(requestSearchParams)) {
        const from = queries?.[queryName]?.queryKey
        const fromPathname = from && requestPathname(from)
        if (!from || !read || fromPathname === undefined) continue

        const to = canonicalQueryKey(from, fromPathname, read(destination))
        // A request that reads `page` asks for page one at the destination, not the page on screen.
        if (hashKey(to) !== hashKey(canonicalQueryKey(from, fromPathname, read(unchanged)))) continue

        if (queryName === 'mainQuery') {
          copyRouteQueryData(queryClient, from, to, savedConfiguration, response.savedView)
          promoted = to
        } else {
          copyRouteQueryData(queryClient, from, to)
        }
      }
      evictPrefetches(promoted)

      const query = destination.toString()
      navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true})
    },
    [evictPrefetches, navigate, pathname, queries, queryClient, requestSearchParams, search],
  )

  const onEditSuccess = useCallback(() => {
    evictPrefetches()
    const {current, destination} = navigationRef.current
    if (destination && destination.pathname !== current.pathname) return true

    const next = new URLSearchParams((destination ?? current).search)
    if (!next.has('q')) return false

    next.delete('q')
    next.delete('page')
    const query = next.toString()
    navigate(query ? `${current.pathname}?${query}` : current.pathname, {preventAutofocus: true})
    return true
  }, [evictPrefetches, navigate])

  return {onSaveSuccess, onEditSuccess}
}
